"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { CaretDown } from "@phosphor-icons/react";

import {
  accountMenuModel,
  type AccountMenuItem,
  type AccountMenuSection,
  type AccountTier,
  type AccountRole,
} from "@/lib/nav/account-menu";
import { PaywallModal } from "./paywall-modal";
import { safeExternalRedirect } from "@/lib/safe-external-redirect";

interface AvatarDropdownProps {
  tier: AccountTier;
  role: AccountRole;
  displayName: string | null;
  initial: string;
  isProvider?: boolean;
  pathname: string;
  /** Number of published camps saved (free-tier "X of N used" hint). */
  savedCount?: number;
}

/**
 * Desktop account dropdown (#1011) — tier-aware, driven by `accountMenuModel`.
 * Click to toggle; click outside, Escape, or selecting an item closes it.
 * Hidden below `md` (mobile uses the slide-out panel in MobileNav).
 */
export function AvatarDropdown({
  tier,
  role,
  displayName,
  initial,
  isProvider,
  pathname,
  savedCount,
}: AvatarDropdownProps) {
  const [open, setOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const model = accountMenuModel({
    tier,
    role,
    displayName,
    savedCount,
    isProvider,
  });

  // Close on route change. pathname is a stable server-passed prop, so the
  // close is bounded by route transitions (canonical pattern, mirrors MobileNav).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see comment above effect
    setOpen(false);
  }, [pathname]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  // Reuse the finder paywall checkout flow (Stripe), same pattern as
  // finder-results.tsx. Upgrade flow itself is out of scope (#1011).
  const handleCheckout = useCallback(async () => {
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const { url } = await res.json();
      if (url) safeExternalRedirect(url);
    } catch (err) {
      console.error("[avatar-dropdown] Checkout failed", err);
    }
  }, []);

  // ── Anonymous: a Sign in link + Create account button, no dropdown. ──
  if (tier === "anonymous") {
    const [signIn, signUp] = model.authActions;
    // accountMenuModel guarantees both for the anonymous tier; guard explicitly
    // so a future model variation can't throw on signUp.href at runtime.
    if (!signIn || !signUp) return null;
    return (
      <>
        <Link
          href={signIn.href}
          className="ml-1 rounded-lg border border-wildroost-ink/15 px-4 py-2 text-sm font-semibold text-wildroost-ink/80 transition-colors hover:border-wildroost-teal hover:text-wildroost-teal active:border-wildroost-teal active:text-wildroost-teal md:text-base"
        >
          {signIn.label}
        </Link>
        <Link
          href={signUp.href}
          className="rounded-lg bg-wildroost-gold px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[var(--gold-hover)] active:bg-[var(--gold-active)] md:text-base"
        >
          {signUp.label}
        </Link>
      </>
    );
  }

  const tierBadge = model.header.tier === "premium" ? "PREMIUM" : "FREE";

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-haspopup="true"
        className="ml-1 flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-wildroost-ink/70 transition-colors hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal md:text-base"
        title={model.header.name}
      >
        <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-wildroost-teal text-xs font-bold text-white">
          {model.header.showDot && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-wildroost-terra" />
          )}
          {initial}
        </span>
        <CaretDown
          size={12}
          weight="bold"
          aria-hidden="true"
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full z-[300] mt-1 w-60 rounded-lg border border-wildroost-ink/10 bg-white py-1 shadow-lg"
          role="menu"
        >
          {/* Header: avatar + name + tier badge — NO email (#1011). */}
          <div className="flex items-center gap-3 border-b border-wildroost-ink/10 px-4 py-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wildroost-teal text-sm font-bold text-white">
              {initial}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-wildroost-ink">
                {model.header.name}
              </div>
              <span
                className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                  model.header.tier === "premium"
                    ? "bg-wildroost-gold/15 text-wildroost-gold"
                    : "bg-wildroost-teal/10 text-wildroost-teal"
                }`}
              >
                {tierBadge}
              </span>
            </div>
          </div>

          {/* Tier-aware sections (My Family, Provider, Admin). */}
          {model.sections.map((section) => (
            <DropdownSection
              key={section.id}
              section={section}
              pathname={pathname}
              onSelect={() => setOpen(false)}
            />
          ))}

          {/* Upgrade CTA (free tier only) — opens the existing PaywallModal. */}
          {model.upgradeCta && (
            <div className="border-t border-wildroost-ink/10 p-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setPaywallOpen(true);
                }}
                className="w-full rounded-lg bg-wildroost-gold px-4 py-2 text-center text-sm font-bold text-white transition-colors hover:bg-[var(--gold-hover)] active:bg-[var(--gold-active)]"
                role="menuitem"
              >
                {model.upgradeCta.label}
              </button>
            </div>
          )}

          {/* Sign out */}
          {model.signOut && (
            <div className="border-t border-wildroost-ink/10 py-1">
              <form method="post" action={model.signOut.href}>
                <button
                  type="submit"
                  className="w-full px-4 py-2 text-left text-sm text-wildroost-ink/70 transition-colors hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
                  role="menuitem"
                >
                  {model.signOut.label}
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Upgrade flow reuses the shared paywall modal (out of scope: #1011). */}
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        trigger="generic"
        onCheckout={handleCheckout}
      />
    </div>
  );
}

/**
 * The current URL hash (e.g. "#saved"), synced on hashchange. Lets the My
 * Family quick-jump items highlight the section being viewed — `pathname` alone
 * never carries the fragment, so `pathname === href` never matches a
 * hash-anchored href like `/my-family#saved`.
 */
function useCurrentHash(): string {
  const [hash, setHash] = useState("");
  useEffect(() => {
    const sync = () => setHash(window.location.hash);
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  return hash;
}

function DropdownSection({
  section,
  pathname,
  onSelect,
}: {
  section: AccountMenuSection;
  pathname: string;
  onSelect: () => void;
}) {
  const currentHash = useCurrentHash();
  return (
    <div className="border-b border-wildroost-ink/10 py-1 last:border-b-0">
      {/* Section label — a link when href is set (My Family -> /my-family). */}
      {section.href ? (
        <Link
          href={section.href}
          onClick={onSelect}
          role="menuitem"
          className="block px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-wildroost-teal/70 transition-colors hover:text-wildroost-teal"
        >
          {section.label}
        </Link>
      ) : (
        <div className="px-4 py-1 text-[10px] font-bold uppercase tracking-wider text-wildroost-teal/60">
          {section.label}
        </div>
      )}
      {section.items.map((item) => (
        <DropdownItem
          key={item.id}
          item={item}
          active={
            item.href.includes("#")
              ? `${pathname}${currentHash}` === item.href
              : pathname === item.href
          }
          onClick={onSelect}
        />
      ))}
    </div>
  );
}

function DropdownItem({
  item,
  active,
  onClick,
}: {
  item: AccountMenuItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Link
      href={item.href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      role="menuitem"
      className={`flex items-center justify-between gap-2 px-4 py-2 text-sm transition-colors ${
        active
          ? "bg-wildroost-teal/10 font-semibold text-wildroost-teal"
          : "text-wildroost-ink/70 hover:bg-wildroost-teal/10 hover:text-wildroost-teal active:bg-wildroost-teal/10 active:text-wildroost-teal"
      }`}
    >
      <span className="flex items-center gap-2">
        {item.label}
        {item.showDot && (
          <span className="h-2 w-2 rounded-full bg-wildroost-terra" aria-label="Needs attention" />
        )}
      </span>
      {item.badge && (
        <span className="shrink-0 text-[11px] font-medium text-wildroost-ink/40">
          {item.badge}
        </span>
      )}
    </Link>
  );
}
